/**
 * Fields & Measures Editor — pill-based field mapping with draggable Y values.
 *
 * Layout per chart type:
 *   pie/funnel:  Category + Value
 *   scatter:     X axis + Y axis + Size (optional)
 *   heatmap:     X axis + Y axis + Value
 *   default:     X axis + Y values (multi, reorderable) + Breakdown (optional)
 *
 * X axis row includes inline Sort + Limit dropdowns when applicable.
 */
import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Input,
  Radio,
  RadioGroup,
  Text,
  Tooltip,
  tokens,
} from "@fluentui/react-components";
import {
  AddRegular,
  ChevronDownRegular,
  Dismiss12Regular,
} from "@fluentui/react-icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import type { DatasetViewColumn } from "../../../api/types";
import type {
  ChartEditorState,
  ChartFieldConfig,
  ChartTypeOption,
  MeasureField,
  TableColumn,
  TableSortEntry,
  AggregationType,
  SortOption,
  SortByField,
} from "../visualConfig";
import {
  BREAKDOWN_RULES,
  DEFAULT_SORT_LIMIT,
  getCapabilities,
  getDefaultAggForColumn,
  shouldShowSortLimit,
} from "../visualConfig";
import {
  SORT_OPTIONS,
  LIMIT_OPTIONS,
  AGGREGATIONS,
  getFieldRoles,
  colLabel,
  colLabelByName,
  measureId,
} from "./constants";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import SortableMeasurePill from "./SortableMeasurePill";
import TableColumnRow from "./TableColumnRow";
import OptionPickerPopover from "./OptionPickerPopover";
import { useFieldMappingStyles } from "./styles";
import { FieldPickerPopover } from "./fieldPicker";

/* ── Sortable sort row (drag-reorderable) ── */
function SortableSortRow({
  id,
  sortEntry,
  index,
  tableColumns,
  allCols,
  styles,
  onChange,
  onRemove,
  showDragHandle,
}: {
  id: string;
  sortEntry: TableSortEntry;
  index: number;
  tableColumns: TableColumn[];
  allCols: DatasetViewColumn[];
  styles: ReturnType<typeof useFieldMappingStyles>;
  onChange: (idx: number, entry: TableSortEntry) => void;
  onRemove: (idx: number) => void;
  showDragHandle: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className={styles.pillRow}>
      {showDragHandle && (
        <span className={styles.measureDrag} {...listeners}>⠿</span>
      )}
      <OptionPickerPopover
        className={styles.dropdown}
        options={tableColumns.map((tc) => ({
          value: tc.column,
          label: colLabelByName(tc.column, allCols),
        }))}
        value={sortEntry.field}
        onSelect={(v) => onChange(index, { ...sortEntry, field: v })}
      />
      <OptionPickerPopover
        className={styles.aggDropdown}
        options={[
          { value: "asc", label: "Asc" },
          { value: "desc", label: "Desc" },
        ]}
        value={sortEntry.direction}
        onSelect={(v) => {
          if (v === "asc" || v === "desc") onChange(index, { ...sortEntry, direction: v });
        }}
      />
      <Button
        appearance="subtle"
        size="small"
        icon={<span>✕</span>}
        onClick={() => onRemove(index)}
        aria-label="Remove sort"
        title="Remove sort"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface FieldMappingEditorProps {
  editorState: ChartEditorState;
  onEditorStateChange: (state: ChartEditorState) => void;
  viewColumns: DatasetViewColumn[];
  chartType: ChartTypeOption;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function FieldMappingEditor({
  editorState,
  onEditorStateChange,
  viewColumns,
  chartType,
}: FieldMappingEditorProps) {
  const styles = useFieldMappingStyles();
  const fields = editorState.fields ?? {};
  const roles = getFieldRoles(chartType);
  const [showCustomLimit, setShowCustomLimit] = useState(false);

  // ── Scatter mode (raw vs aggregated) ──────────────────────────────
  const scatterMode = editorState.chartFieldsConfig.scatterMode ?? "raw";
  const isScatterRaw = roles.showModeToggle && scatterMode === "raw";
  const hideAggregation = isScatterRaw || roles.hideAggregation;

  const handleScatterModeChange = useCallback(
    (mode: "raw" | "aggregated") => {
      onEditorStateChange({
        ...editorState,
        chartFieldsConfig: { ...editorState.chartFieldsConfig, scatterMode: mode },
      });
    },
    [editorState, onEditorStateChange],
  );

  const updateFields = useCallback(
    (next: ChartFieldConfig) => {
      onEditorStateChange({ ...editorState, fields: next });
    },
    [editorState, onEditorStateChange],
  );

  // ── Column lists (declared early — used by sort logic below) ──────
  const allCols = viewColumns;
  const yFields = fields.y ?? [];

  // ── Sort & Limit ──────────────────────────────────────────────────
  const xCol = fields.x?.column;
  const xSemantic = useMemo(
    () => viewColumns.find((c) => c.name === xCol)?.semantic_type,
    [viewColumns, xCol],
  );
  const showSortLimit =
    Boolean(xCol) && shouldShowSortLimit(chartType, xSemantic);

  const defaults = DEFAULT_SORT_LIMIT[chartType];
  const fc = editorState.chartFieldsConfig;
  const effectiveSort: SortOption = fc.sort ?? defaults.sort;
  const effectiveLimit: number | null =
    fc.limit !== undefined ? fc.limit : defaults.limit;
  const effectiveLimitOthers: boolean =
    fc.limitOthers !== undefined
      ? fc.limitOthers
      : defaults.limitOthers;

  // ── Build sort field options for the sort-by-field dropdown ────────
  // Values use "column::aggregation" for Y measures to avoid collisions
  // when the same column appears with different aggregations.
  const sortFieldOptions = useMemo(() => {
    const opts: { value: string; label: string; column: string }[] = [];

    // Y measures — composite key to handle SUM(x) + AVG(x) on same column
    for (const yf of yFields) {
      const col = allCols.find((c) => c.name === yf.column);
      const displayName = col ? colLabel(col) : yf.column;
      const aggLabel = AGGREGATIONS.find((a) => a.value === yf.aggregation)?.label ?? yf.aggregation;
      opts.push({
        value: `${yf.column}::${yf.aggregation}`,
        label: `${aggLabel} ${displayName}`,
        column: yf.column,
      });
    }

    // X dimension
    if (xCol) {
      const col = allCols.find((c) => c.name === xCol);
      const displayName = col ? colLabel(col) : xCol;
      opts.push({ value: `x::${xCol}`, label: displayName, column: xCol });
    }

    return opts;
  }, [yFields, xCol, allCols]);

  /** Update the explicit sort-by-field. Clears legacy `sort` strategy.
   *  `field` is the composite dropdown value (e.g. "col::sum" or "x::col").
   *  We extract the raw column name for backend storage. */
  const updateSortByField = useCallback(
    (sortByField: SortByField | null) => {
      if (!sortByField || sortByField.field === "__none__") {
        onEditorStateChange({
          ...editorState,
          chartFieldsConfig: {
            ...editorState.chartFieldsConfig,
            sortField: undefined,
            sortDirection: undefined,
          },
        });
      } else {
        const compositeKey = sortByField.field;
        const opt = sortFieldOptions.find((o) => o.value === compositeKey);
        const rawColumn = opt?.column ?? compositeKey.split("::")[0];
        onEditorStateChange({
          ...editorState,
          chartFieldsConfig: {
            ...editorState.chartFieldsConfig,
            sortField: rawColumn,
            sortDirection: sortByField.direction,
          },
        });
      }
    },
    [editorState, onEditorStateChange, sortFieldOptions],
  );

  // Resolve current sort field value from sortField or legacy sort
  const effectiveSortField = useMemo((): string => {
    const stored = fc.sortField;
    if (stored) {
      // Find option whose column matches the stored sortField
      const match = sortFieldOptions.find((o) => o.column === stored);
      if (match) return match.value;

      // Fallback: match against Y field labels (handles SQL alias stored as sortField)
      const yMatch = yFields.find((yf) => yf.label === stored);
      if (yMatch) {
        const opt = sortFieldOptions.find((o) => o.column === yMatch.column);
        if (opt) return opt.value;
      }

      return "__none__";
    }
    // Map legacy sort to a field value
    if (!effectiveSort || effectiveSort === "none") return "__none__";
    if (effectiveSort === "name_asc" || effectiveSort === "name_desc") {
      const xOpt = sortFieldOptions.find((o) => o.column === xCol);
      return xOpt?.value ?? "__none__";
    }
    // value_desc / value_asc → first Y measure
    return sortFieldOptions[0]?.value ?? "__none__";
  }, [fc.sortField, effectiveSort, xCol, sortFieldOptions, yFields]);

  const effectiveSortDirection = useMemo((): "asc" | "desc" => {
    if (fc.sortDirection) return fc.sortDirection;
    if (effectiveSort === "value_asc" || effectiveSort === "name_asc") return "asc";
    return "desc";
  }, [fc.sortDirection, effectiveSort]);

  const updateLimit = useCallback(
    (value: number | null) => {
      onEditorStateChange({
        ...editorState,
        chartFieldsConfig: {
          ...editorState.chartFieldsConfig,
          limit: value,
          ...(value === null ? { limitOthers: false } : {}),
        },
      });
    },
    [editorState, onEditorStateChange],
  );

  const updateLimitOthers = useCallback(
    (value: boolean) => {
      onEditorStateChange({
        ...editorState,
        chartFieldsConfig: { ...editorState.chartFieldsConfig, limitOthers: value },
      });
    },
    [editorState, onEditorStateChange],
  );

  const limitDropdownValue = useMemo(() => {
    if (effectiveLimit === null || effectiveLimit === undefined) return "none";
    const preset = LIMIT_OPTIONS.find(
      (o) => o.value === String(effectiveLimit),
    );
    return preset ? preset.value : "custom";
  }, [effectiveLimit]);

  const breakdownRule = BREAKDOWN_RULES[chartType];
  const breakdownCols = useMemo(
    () =>
      breakdownRule.enabled
        ? viewColumns.filter((c) =>
            breakdownRule.allowed.includes(c.semantic_type),
          )
        : [],
    [viewColumns, breakdownRule],
  );

  // ── X / Category ─────────────────────────────────────────────────
  const handleXChange = useCallback(
    (colName: string | undefined) => {
      if (!colName) {
        const { x: _, ...rest } = fields;
        updateFields(rest);
      } else {
        updateFields({ ...fields, x: { column: colName } });
      }
    },
    [fields, updateFields],
  );

  // ── Y Measures ───────────────────────────────────────────────────

  const handleYChange = useCallback(
    (idx: number, measure: MeasureField) => {
      const next = [...yFields];
      next[idx] = measure;
      updateFields({ ...fields, y: next });
    },
    [yFields, fields, updateFields],
  );

  const handleYRemove = useCallback(
    (idx: number) => {
      const next = yFields.filter((_, i) => i !== idx);
      updateFields({ ...fields, y: next.length ? next : undefined });
    },
    [yFields, fields, updateFields],
  );

  const handleYAdd = useCallback(() => {
    const usedCols = new Set(yFields.map((y) => y.column));
    const measureCols = allCols.filter((c) => c.semantic_type === "measure");
    const available =
      measureCols.find((c) => !usedCols.has(c.name)) ??
      measureCols[0] ??
      allCols[0];
    if (!available) return;
    updateFields({
      ...fields,
      y: [
        ...yFields,
        {
          column: available.name,
          aggregation: getDefaultAggForColumn(available),
        },
      ],
    });
  }, [yFields, allCols, fields, updateFields]);

  // ── Series / Breakdown ───────────────────────────────────────────
  const handleSeriesChange = useCallback(
    (colName: string | undefined) => {
      if (!colName) {
        const { series: _, ...rest } = fields;
        onEditorStateChange({
          ...editorState,
          fields: rest,
          chartStyle: { ...editorState.chartStyle, breakdownSort: "none" },
        });
      } else {
        updateFields({ ...fields, series: { column: colName } });
      }
    },
    [fields, updateFields, editorState, onEditorStateChange],
  );

  const handleBreakdownSortChange = useCallback(
    (value: SortOption) => {
      onEditorStateChange({
        ...editorState,
        chartStyle: { ...editorState.chartStyle, breakdownSort: value },
      });
    },
    [editorState, onEditorStateChange],
  );

  // ── Y Dimension (heatmap) ────────────────────────────────────────
  const handleYDimensionChange = useCallback(
    (colName: string | undefined) => {
      if (!colName) {
        const { yDimension: _, ...rest } = fields;
        updateFields(rest);
      } else {
        updateFields({ ...fields, yDimension: { column: colName } });
      }
    },
    [fields, updateFields],
  );

  // ── Size (scatter/bubble) ────────────────────────────────────────
  const handleSizeChange = useCallback(
    (colName: string | undefined) => {
      if (!colName) {
        const { size: _, ...rest } = fields;
        updateFields(rest);
      } else {
        const col = allCols.find((c) => c.name === colName);
        const agg = col
          ? getDefaultAggForColumn(col)
          : ("sum" as AggregationType);
        updateFields({
          ...fields,
          size: { column: colName, aggregation: agg },
        });
      }
    },
    [fields, updateFields, allCols],
  );

  const handleSizeAggChange = useCallback(
    (agg: AggregationType) => {
      if (!fields.size) return;
      updateFields({ ...fields, size: { ...fields.size, aggregation: agg } });
    },
    [fields, updateFields],
  );

  const supportsBreakdownSort = getCapabilities(chartType).stacking;

  // ── DnD setup ────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const sortableIds = useMemo(
    () => yFields.map((_, i) => measureId(i)),
    [yFields],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = sortableIds.indexOf(active.id as string);
      const newIndex = sortableIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(yFields, oldIndex, newIndex);
      updateFields({ ...fields, y: reordered });
    },
    [sortableIds, yFields, fields, updateFields],
  );

  // ── KPI-specific handlers ─────────────────────────────────────────
  const kpiValue = fields.value;

  const handleKpiFieldChange = useCallback(
    (colName: string | undefined) => {
      if (!colName) {
        const { value: _, ...rest } = fields;
        updateFields(rest);
      } else {
        const col = allCols.find((c) => c.name === colName);
        const agg = col
          ? getDefaultAggForColumn(col)
          : ("sum" as AggregationType);
        updateFields({
          ...fields,
          value: { column: colName, aggregation: agg },
        });
      }
    },
    [fields, updateFields, allCols],
  );

  const handleKpiAggChange = useCallback(
    (agg: AggregationType) => {
      if (!kpiValue) return;
      updateFields({
        ...fields,
        value: { ...kpiValue, aggregation: agg },
      });
    },
    [kpiValue, fields, updateFields],
  );

  // ── Table-specific handlers ───────────────────────────────────────
  const tableColumns = fields.columns ?? [];

  const handleTableColumnChange = useCallback(
    (idx: number, col: TableColumn) => {
      const next = [...tableColumns];
      next[idx] = col;
      updateFields({ ...fields, columns: next });
    },
    [tableColumns, fields, updateFields],
  );

  const handleTableColumnRemove = useCallback(
    (idx: number) => {
      const removedCol = tableColumns[idx]?.column;
      const next = tableColumns.filter((_, i) => i !== idx);
      // Auto-remove from sorting when column is removed
      const currentSorting = fields.sorting ?? [];
      const nextSorting = removedCol
        ? currentSorting.filter((s) => s.field !== removedCol)
        : currentSorting;
      updateFields({
        ...fields,
        columns: next.length ? next : undefined,
        sorting: nextSorting.length ? nextSorting : undefined,
      });
    },
    [tableColumns, fields, updateFields],
  );

  const handleTableColumnAdd = useCallback(() => {
    const usedCols = new Set(tableColumns.map((c) => c.column));
    // Prefer first unused dimension, then first unused measure, then first unused column
    const dimCols = allCols.filter((c) => c.semantic_type !== "measure" && !usedCols.has(c.name));
    const mesCols = allCols.filter((c) => c.semantic_type === "measure" && !usedCols.has(c.name));
    const available = dimCols[0] ?? mesCols[0] ?? allCols.find((c) => !usedCols.has(c.name)) ?? allCols[0];
    if (!available) return;
    const agg: AggregationType =
      available.semantic_type === "measure" ? "sum" : "none";
    updateFields({
      ...fields,
      columns: [...tableColumns, { column: available.name, aggregation: agg }],
    });
  }, [tableColumns, allCols, fields, updateFields]);

  const handleTableColumnDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = tableColumns.map((_, i) => `table-col-${i}`);
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(tableColumns, oldIndex, newIndex);
      updateFields({ ...fields, columns: reordered });
    },
    [tableColumns, fields, updateFields],
  );

  const tableColIds = useMemo(
    () => tableColumns.map((_, i) => `table-col-${i}`),
    [tableColumns],
  );

  // ── Table sorting handlers ────────────────────────────────────────
  const tableSorting = fields.sorting ?? [];

  const tableSortIds = useMemo(
    () => tableSorting.map((_, i) => `table-sort-${i}`),
    [tableSorting],
  );

  const handleTableSortDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = tableSortIds.indexOf(active.id as string);
      const newIndex = tableSortIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(tableSorting, oldIndex, newIndex);
      updateFields({ ...fields, sorting: reordered });
    },
    [tableSortIds, tableSorting, fields, updateFields],
  );

  const handleTableSortAdd = useCallback(() => {
    const usedSorts = new Set(tableSorting.map((s) => s.field));
    const available = tableColumns.find((c) => !usedSorts.has(c.column));
    if (!available) return;
    updateFields({
      ...fields,
      sorting: [...tableSorting, { field: available.column, direction: "desc" as const }],
    });
  }, [tableSorting, tableColumns, fields, updateFields]);

  const handleTableSortChange = useCallback(
    (idx: number, entry: TableSortEntry) => {
      const next = [...tableSorting];
      next[idx] = entry;
      updateFields({ ...fields, sorting: next });
    },
    [tableSorting, fields, updateFields],
  );

  const handleTableSortRemove = useCallback(
    (idx: number) => {
      const next = tableSorting.filter((_, i) => i !== idx);
      updateFields({ ...fields, sorting: next.length ? next : undefined });
    },
    [tableSorting, fields, updateFields],
  );

  // ── Render ────────────────────────────────────────────────────────
  const showDrag = roles.multiMeasure && yFields.length > 1;

  // ── KPI: dedicated single-value UI ────────────────────────────────
  if (chartType === "kpi") {
    return (
      <div className={styles.root}>
        <div className={styles.fieldGroup}>
          <Text size={200} weight="semibold" className={styles.fieldLabel}>
            Value
          </Text>
          <div className={styles.pillRow}>
            <FieldPickerPopover
              columns={allCols}
              value={kpiValue?.column}
              onSelect={(v) => handleKpiFieldChange(v)}
            >
              <Button
                className={styles.dropdown}
                size="small"
                appearance="subtle"
                iconPosition="after"
                icon={<ChevronDownRegular />}
              >
                {kpiValue?.column
                  ? colLabelByName(kpiValue.column, allCols)
                  : "Select field…"}
              </Button>
            </FieldPickerPopover>
            {kpiValue?.column && (
              <OptionPickerPopover
                className={styles.aggDropdown}
                options={AGGREGATIONS}
                value={kpiValue.aggregation}
                onSelect={(v) => handleKpiAggChange(v as AggregationType)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Table: dedicated column-list UI ───────────────────────────────
  if (chartType === "table") {
    const showTableDrag = tableColumns.length > 1;
    return (
      <div className={styles.root}>
        <div className={styles.fieldGroup}>
          <div className={styles.fieldGroupHeader}>
            <Text size={200} weight="semibold" className={styles.fieldLabel}>
              Columns
            </Text>
            {tableColumns.length > 0 && (
              <Button
                appearance="subtle"
                size="small"
                icon={<AddRegular />}
                onClick={handleTableColumnAdd}
              >
                Add column
              </Button>
            )}
          </div>
          <div className={styles.measureList}>
            {tableColumns.length === 0 ? (
              <Button
                appearance="subtle"
                size="small"
                icon={<AddRegular />}
                onClick={handleTableColumnAdd}
              >
                Add column
              </Button>
            ) : showTableDrag ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleTableColumnDragEnd}
              >
                <SortableContext
                  items={tableColIds}
                  strategy={verticalListSortingStrategy}
                >
                  {tableColumns.map((col, i) => (
                    <TableColumnRow
                      key={tableColIds[i]}
                      id={tableColIds[i]}
                      col={col}
                      index={i}
                      allCols={allCols}
                      styles={styles}
                      onChange={handleTableColumnChange}
                      onRemove={handleTableColumnRemove}
                      showDragHandle
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              tableColumns.map((col, i) => (
                <TableColumnRow
                  key={`table-col-${i}`}
                  id={`table-col-${i}`}
                  col={col}
                  index={i}
                  allCols={allCols}
                  styles={styles}
                  onChange={handleTableColumnChange}
                  onRemove={handleTableColumnRemove}
                  showDragHandle={false}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Sorting ── */}
        <div className={styles.fieldGroup}>
          <div className={styles.fieldGroupHeader}>
            <Text size={200} weight="semibold" className={styles.fieldLabel}>
              Sorting
            </Text>
            {tableSorting.length > 0 && tableSorting.length < tableColumns.length && (
              <Button
                appearance="subtle"
                size="small"
                icon={<AddRegular />}
                onClick={handleTableSortAdd}
              >
                Add sort
              </Button>
            )}
          </div>
          <div className={styles.measureList}>
            {tableSorting.length === 0 ? (
              <Button
                appearance="subtle"
                size="small"
                icon={<AddRegular />}
                onClick={handleTableSortAdd}
              >
                Add sort
              </Button>
            ) : tableSorting.length > 1 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleTableSortDragEnd}
              >
                <SortableContext
                  items={tableSortIds}
                  strategy={verticalListSortingStrategy}
                >
                  {tableSorting.map((sortEntry, i) => (
                    <SortableSortRow
                      key={tableSortIds[i]}
                      id={tableSortIds[i]}
                      sortEntry={sortEntry}
                      index={i}
                      tableColumns={tableColumns}
                      allCols={allCols}
                      styles={styles}
                      onChange={handleTableSortChange}
                      onRemove={handleTableSortRemove}
                      showDragHandle
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              tableSorting.map((sortEntry, i) => (
                <SortableSortRow
                  key={`table-sort-${i}`}
                  id={`table-sort-${i}`}
                  sortEntry={sortEntry}
                  index={i}
                  tableColumns={tableColumns}
                  allCols={allCols}
                  styles={styles}
                  onChange={handleTableSortChange}
                  onRemove={handleTableSortRemove}
                  showDragHandle={false}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Row limit ── */}
        <div className={styles.fieldGroup}>
          <Text size={200} weight="semibold" className={styles.fieldLabel}>
            Row limit
          </Text>
          <div className={styles.limitRow}>
            {showCustomLimit ? (
              <Input
                className={styles.customLimitInput}
                size="medium"
                type="number"
                min={1}
                max={10000}
                value={String(effectiveLimit ?? 100)}
                onChange={(_, data) => {
                  const n = parseInt(data.value, 10);
                  if (!isNaN(n) && n > 0) updateLimit(n);
                }}
                onBlur={() => setShowCustomLimit(false)}
              />
            ) : (
              <OptionPickerPopover
                className={styles.limitDropdown}
                options={LIMIT_OPTIONS}
                value={limitDropdownValue}
                prefix="Limit: "
                onSelect={(v) => {
                  if (v === "none") {
                    updateLimit(null);
                    setShowCustomLimit(false);
                  } else if (v === "custom") {
                    setShowCustomLimit(true);
                    if (effectiveLimit === null) updateLimit(100);
                  } else {
                    updateLimit(parseInt(v, 10));
                    setShowCustomLimit(false);
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Standard X/Y/Breakdown UI (all other chart types) ─────────────
  return (
    <div className={styles.root}>
      {/* ── Scatter mode toggle ── */}
      {roles.showModeToggle && (
        <div className={styles.fieldGroup}>
          <Text size={200} weight="semibold" className={styles.fieldLabel}>
            Mode
          </Text>
          <RadioGroup
            layout="horizontal"
            value={scatterMode}
            onChange={(_, data) => handleScatterModeChange(data.value as "raw" | "aggregated")}
          >
            <Radio value="raw" label="Raw points" />
            <Radio value="aggregated" label="Aggregated" />
          </RadioGroup>
          {isScatterRaw && (
            <span className={styles.fieldHelper}>
              Each row is shown as a point (no aggregation)
            </span>
          )}
        </div>
      )}

      {/* ── X / Category row (with inline Sort + Limit) ── */}
      <div className={styles.fieldGroup}>
        <Text size={200} weight="semibold" className={styles.fieldLabel}>
          {roles.xLabel}
        </Text>
        {showSortLimit ? (
          <div className={styles.xAxisBox}>
            {/* Row 1: Category dropdown */}
            <div className={styles.xAxisCategoryRow}>
              <FieldPickerPopover
                columns={allCols}
                value={xCol}
                onSelect={(v) => handleXChange(v)}
              >
                <Button
                  className={styles.dropdown}
                  size="small"
                  appearance="subtle"
                  iconPosition="after"
                  icon={<ChevronDownRegular />}
                >
                  {xCol ? colLabelByName(xCol, allCols) : "Select column…"}
                </Button>
              </FieldPickerPopover>
            </div>

            {/* Row 2: Sort + Limit + Others */}
            <div className={styles.sortLimitRow}>
              <OptionPickerPopover
                className={styles.sortDropdown}
                options={[
                  { value: "__none__", label: "None" },
                  ...sortFieldOptions,
                ]}
                value={effectiveSortField}
                prefix="Sort: "
                onSelect={(v) => {
                  if (v === "__none__") {
                    updateSortByField(null);
                  } else {
                    updateSortByField({ field: v, direction: effectiveSortDirection });
                  }
                }}
              />

              {effectiveSortField !== "__none__" && (
                <Button
                  size="small"
                  appearance="subtle"
                  style={{ minWidth: 24, padding: "0 2px", fontWeight: 600, flexShrink: 0 }}
                  onClick={() => {
                    const newDir = effectiveSortDirection === "desc" ? "asc" : "desc";
                    updateSortByField({ field: effectiveSortField, direction: newDir });
                  }}
                  title={effectiveSortDirection === "desc" ? "Descending — click to toggle" : "Ascending — click to toggle"}
                >
                  {effectiveSortDirection === "desc" ? "↓" : "↑"}
                </Button>
              )}

              {showCustomLimit ? (
                <Input
                  className={styles.customLimitInput}
                  size="medium"
                  type="number"
                  min={1}
                  max={1000}
                  value={String(effectiveLimit ?? 10)}
                  onChange={(_, data) => {
                    const n = parseInt(data.value, 10);
                    if (!isNaN(n) && n > 0) updateLimit(n);
                  }}
                  onBlur={() => setShowCustomLimit(false)}
                />
              ) : (
                <OptionPickerPopover
                  className={styles.limitDropdown}
                  options={LIMIT_OPTIONS}
                  value={limitDropdownValue}
                  prefix="Limit: "
                  onSelect={(v) => {
                    if (v === "none") {
                      updateLimit(null);
                      setShowCustomLimit(false);
                    } else if (v === "custom") {
                      setShowCustomLimit(true);
                      if (effectiveLimit === null) updateLimit(10);
                    } else {
                      updateLimit(parseInt(v, 10));
                      setShowCustomLimit(false);
                    }
                  }}
                />
              )}

              {effectiveLimit !== null && (
                <Tooltip
                  content="Group remaining categories into 'Others'"
                  relationship="description"
                >
                  <Checkbox
                    className={styles.othersCheckbox}
                    size="medium"
                    label="Others"
                    checked={effectiveLimitOthers}
                    onChange={(_, data) =>
                      updateLimitOthers(data.checked === true)
                    }
                  />
                </Tooltip>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.pillRow}>
            <FieldPickerPopover
              columns={allCols}
              value={xCol}
              onSelect={(v) => handleXChange(v)}
            >
              <Button
                className={styles.dropdown}
                size="small"
                appearance="subtle"
                iconPosition="after"
                icon={<ChevronDownRegular />}
              >
                {xCol ? colLabelByName(xCol, allCols) : "Select column…"}
              </Button>
            </FieldPickerPopover>
          </div>
        )}
      </div>

      {/* ── Y Dimension (heatmap: second dimension for the grid) ── */}
      {roles.yDimensionLabel && (
        <div className={styles.fieldGroup}>
          <Text size={200} weight="semibold" className={styles.fieldLabel}>
            {roles.yDimensionLabel}
          </Text>
          <div className={styles.pillRow}>
            <FieldPickerPopover
              columns={allCols}
              value={fields.yDimension?.column}
              onSelect={(v) => handleYDimensionChange(v)}
            >
              <Button
                className={styles.dropdown}
                size="small"
                appearance="subtle"
                iconPosition="after"
                icon={<ChevronDownRegular />}
              >
                {fields.yDimension?.column
                  ? colLabelByName(fields.yDimension.column, allCols)
                  : "Select column…"}
              </Button>
            </FieldPickerPopover>
          </div>
        </div>
      )}

      {/* ── Y / Values ── */}
      <div className={styles.fieldGroup}>
        <div className={styles.fieldGroupHeader}>
          <Text size={200} weight="semibold" className={styles.fieldLabel}>
            {roles.yLabel}
          </Text>
          {roles.multiMeasure && yFields.length > 0 && (
            <Button
              appearance="subtle"
              size="small"
              icon={<AddRegular />}
              onClick={handleYAdd}
            >
              Add measure
            </Button>
          )}
        </div>
        {roles.yAutoCount ? (
          <Text
            size={200}
            style={{
              color: tokens.colorNeutralForeground3,
              padding: "6px 8px",
            }}
          >
            Automatic count of values in each bin
          </Text>
        ) : (
          <div className={styles.measureList}>
            {yFields.length === 0 ? (
              <Button
                appearance="subtle"
                size="small"
                icon={<AddRegular />}
                onClick={handleYAdd}
              >
                {roles.multiMeasure ? "Add measure" : "Add value"}
              </Button>
            ) : showDrag ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sortableIds}
                  strategy={verticalListSortingStrategy}
                >
                  {yFields.map((m, i) => (
                    <SortableMeasurePill
                      key={measureId(i)}
                      id={measureId(i)}
                      measure={m}
                      columns={allCols}
                      viewColumns={allCols}
                      onChange={(updated) => handleYChange(i, updated)}
                      onRemove={() => handleYRemove(i)}
                      canRemove={yFields.length > 1}
                      showDragHandle
                      hideAggregation={hideAggregation}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              yFields.map((m, i) => (
                <SortableMeasurePill
                  key={measureId(i)}
                  id={measureId(i)}
                  measure={m}
                  columns={allCols}
                  viewColumns={allCols}
                  onChange={(updated) => handleYChange(i, updated)}
                  onRemove={() => handleYRemove(i)}
                  canRemove={!roles.multiMeasure}
                  showDragHandle={false}
                  hideAggregation={hideAggregation}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* ── Series / Breakdown (with optional Sort) ── */}
      {roles.seriesLabel !== null && breakdownCols.length > 0 && (
        <div className={styles.fieldGroup}>
          <Text size={200} weight="semibold" className={styles.fieldLabel}>
            {roles.seriesLabel}
          </Text>
          {roles.seriesHelper && (
            <span className={styles.fieldHelper}>{roles.seriesHelper}</span>
          )}
          <div className={styles.pillRow}>
            <FieldPickerPopover
              columns={breakdownCols}
              value={fields.series?.column}
              onSelect={(v) => handleSeriesChange(v)}
            >
              <Button
                className={styles.dropdown}
                size="small"
                appearance="subtle"
                iconPosition="after"
                icon={<ChevronDownRegular />}
              >
                {fields.series?.column
                  ? colLabelByName(fields.series.column, breakdownCols)
                  : "Select column…"}
              </Button>
            </FieldPickerPopover>

            {fields.series?.column && supportsBreakdownSort && (
              <Tooltip
                content="Sorting is applied client-side and not reflected in the SQL query"
                relationship="description"
              >
                <OptionPickerPopover
                  className={styles.sortDropdown}
                  options={SORT_OPTIONS}
                  value={editorState.chartStyle.breakdownSort ?? "none"}
                  prefix="Sort: "
                  onSelect={(v) => handleBreakdownSortChange(v as SortOption)}
                />
              </Tooltip>
            )}
            {fields.series?.column && (
              <Button
                appearance="subtle"
                size="small"
                icon={<Dismiss12Regular />}
                aria-label="Remove breakdown"
                title="Remove breakdown"
                onClick={() => handleSeriesChange(undefined)}
              />
            )}
          </div>
        </div>
      )}

      {/* ── Size (scatter/bubble) ── */}
      {roles.showSize && (
        <div className={styles.fieldGroup}>
          <Text size={200} weight="semibold" className={styles.fieldLabel}>
            Size
          </Text>
          <span className={styles.fieldHelper}>
            Optional — controls point size
          </span>
          <div className={styles.pillRow}>
            <FieldPickerPopover
              columns={allCols}
              value={fields.size?.column}
              onSelect={(v) => handleSizeChange(v)}
            >
              <Button
                className={styles.dropdown}
                size="small"
                appearance="subtle"
                iconPosition="after"
                icon={<ChevronDownRegular />}
              >
                {fields.size?.column
                  ? colLabelByName(fields.size.column, allCols)
                  : "— None —"}
              </Button>
            </FieldPickerPopover>
            {fields.size?.column && !hideAggregation && (
              <OptionPickerPopover
                className={styles.aggDropdown}
                options={AGGREGATIONS}
                value={fields.size.aggregation}
                onSelect={(v) => handleSizeAggChange(v as AggregationType)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}